(() => {
  const STORAGE_PREFIX = 'gt-mobile-bottom-nav:';
  const LIVE_INTERVAL = 30000;

  function readStorage(key, fallback = null) {
    try {
      const value = window.localStorage.getItem(key);
      return value == null ? fallback : value;
    } catch (error) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {}
  }

  function formatMoney(cents) {
    const amount = (Number(cents || 0) / 100);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  function setup(root) {
    if (!root || root.dataset.gtMbnReady === 'true') return;
    root.dataset.gtMbnReady = 'true';

    const sectionId = root.dataset.sectionId || root.id || window.location.pathname;
    const allowDrag = root.dataset.allowDrag === 'true';
    const remember = root.dataset.rememberState === 'true';
    const collapseStorageKey = STORAGE_PREFIX + sectionId + ':collapsed';
    const rewardStorageKey = STORAGE_PREFIX + sectionId + ':reward';
    const panel = root.querySelector('[data-gt-mbn-panel]');
    const handles = root.querySelectorAll('[data-gt-mbn-handle], [data-gt-mbn-nav-handle]');
    const steps = Array.from(root.querySelectorAll('[data-gt-mbn-step]'));
    const toast = root.querySelector('[data-gt-mbn-toast]');
    const toastTitle = toast?.querySelector('[data-gt-mbn-toast-title]');
    const toastMessage = toast?.querySelector('[data-gt-mbn-toast-message]');
    const toastClose = toast?.querySelector('[data-gt-mbn-toast-close]');
    const currentAmountEl = root.querySelector('[data-gt-mbn-current-amount]');
    const goalAmountEl = root.querySelector('[data-gt-mbn-goal-amount]');
    const progressFillEl = root.querySelector('[data-gt-mbn-progress-fill]');
    const progressMessageEl = root.querySelector('[data-gt-mbn-progress-message]');
    const cartBadgeEls = Array.from(root.querySelectorAll('[data-gt-mbn-cart-badge]'));
    const popupEnabled = root.dataset.popupEnabled === 'true';
    const popupDuration = parseInt(root.dataset.popupDuration || '4000', 10) || 4000;
    const goalCents = parseInt(root.dataset.goalCents || '0', 10) || 0;
    const currencySymbol = root.dataset.currencySymbol || '';
    const progressMessageTemplate = root.dataset.progressMessageTemplate || 'Add [amount] [currency] to unlock';
    const progressSuccessTemplate = root.dataset.progressSuccessTemplate || 'Target reached';

    const state = {
      currentCents: parseInt(root.dataset.currentCents || '0', 10) || 0,
      itemCount: 0,
      startY: 0,
      lastY: 0,
      tracking: false,
      moved: false,
      hideTimer: null,
      refreshQueued: false,
      liveIntervalId: null,
    };

    const savedCollapse = remember ? readStorage(collapseStorageKey) : null;
    const collapsed = savedCollapse ? savedCollapse === 'collapsed' : root.dataset.defaultCollapsed === 'true';
    setCollapsed(collapsed, false);
    renderState();

    function setCollapsed(value, save = true) {
      root.classList.toggle('is-collapsed', value);
      handles.forEach((handle) => handle.setAttribute('aria-expanded', String(!value)));
      if (save && remember) writeStorage(collapseStorageKey, value ? 'collapsed' : 'open');
    }

    function toggle() {
      setCollapsed(!root.classList.contains('is-collapsed'));
    }

    function onPointerDown(event) {
      if (!allowDrag) return;
      state.tracking = true;
      state.moved = false;
      state.startY = event.clientY || 0;
      state.lastY = state.startY;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }

    function onPointerMove(event) {
      if (!state.tracking) return;
      state.lastY = event.clientY || state.lastY;
      if (Math.abs(state.lastY - state.startY) > 8) state.moved = true;
      if (panel && state.moved) {
        const delta = Math.max(-60, Math.min(90, state.lastY - state.startY));
        if (!root.classList.contains('is-collapsed')) {
          panel.style.transform = `translateY(${Math.max(0, delta)}px)`;
        }
      }
    }

    function onPointerUp() {
      if (!state.tracking) return;
      state.tracking = false;
      if (panel) panel.style.transform = '';
      const delta = state.lastY - state.startY;
      if (state.moved && Math.abs(delta) > 28) {
        setCollapsed(delta > 0);
      }
      window.setTimeout(() => { state.moved = false; }, 0);
    }

    handles.forEach((handle) => {
      handle.addEventListener('click', (event) => {
        event.preventDefault();
        if (!state.moved) toggle();
      });
      handle.addEventListener('pointerdown', onPointerDown);
      handle.addEventListener('pointermove', onPointerMove);
      handle.addEventListener('pointerup', onPointerUp);
      handle.addEventListener('pointercancel', onPointerUp);
    });

    function hideToast() {
      if (!toast) return;
      toast.classList.remove('is-visible');
      window.clearTimeout(state.hideTimer);
      window.setTimeout(() => {
        if (!toast.classList.contains('is-visible')) toast.hidden = true;
      }, 220);
    }

    function showToast(title, message) {
      if (!toast) return;
      if (toastTitle) toastTitle.textContent = title;
      if (toastMessage) toastMessage.textContent = message;
      toast.hidden = false;
      window.requestAnimationFrame(() => toast.classList.add('is-visible'));
      window.clearTimeout(state.hideTimer);
      state.hideTimer = window.setTimeout(hideToast, popupDuration);
    }

    toastClose?.addEventListener('click', hideToast);

    function markStep(stepEl) {
      if (!stepEl) return;
      stepEl.classList.add('is-just-achieved');
      window.setTimeout(() => stepEl.classList.remove('is-just-achieved'), 1600);
    }

    function getProgressPercent() {
      if (!goalCents) return 0;
      return Math.max(0, Math.min(100, (state.currentCents / goalCents) * 100));
    }

    function updateMessage() {
      if (!progressMessageEl) return;
      const remainingCents = Math.max(0, goalCents - state.currentCents);
      if (remainingCents > 0) {
        progressMessageEl.textContent = progressMessageTemplate
          .replace(/\[amount\]/g, formatMoney(remainingCents))
          .replace(/\[currency\]/g, currencySymbol);
      } else {
        progressMessageEl.textContent = progressSuccessTemplate;
      }
    }

    function renderState() {
      if (currentAmountEl) currentAmountEl.textContent = formatMoney(state.currentCents);
      if (goalAmountEl) goalAmountEl.textContent = formatMoney(goalCents);
      if (progressFillEl) progressFillEl.style.width = `${getProgressPercent()}%`;
      cartBadgeEls.forEach((badgeEl) => {
        if (state.itemCount > 0) {
          badgeEl.textContent = String(state.itemCount);
          badgeEl.hidden = false;
          badgeEl.style.display = 'inline-flex';
        } else {
          badgeEl.hidden = true;
          badgeEl.style.display = 'none';
        }
      });
      steps.forEach((stepEl) => {
        const target = parseInt(stepEl.dataset.stepTargetCents || '0', 10) || 0;
        stepEl.classList.toggle('is-active', state.currentCents >= target);
      });
      updateMessage();
    }

    function evaluateRewards(previousCents = 0) {
      if (!popupEnabled || !steps.length) return;

      const achievedSteps = steps.filter((step) => {
        const target = parseInt(step.dataset.stepTargetCents || '0', 10) || 0;
        return state.currentCents >= target;
      });

      const previousSteps = steps.filter((step) => {
        const target = parseInt(step.dataset.stepTargetCents || '0', 10) || 0;
        return previousCents >= target;
      });

      const finalReached = goalCents > 0 && state.currentCents >= goalCents;
      const previousFinalReached = goalCents > 0 && previousCents >= goalCents;
      const currentLevel = finalReached ? steps.length + 1 : achievedSteps.length;
      const previousLevel = previousFinalReached ? steps.length + 1 : previousSteps.length;
      let savedLevel = parseInt(readStorage(rewardStorageKey, '0') || '0', 10);
      if (Number.isNaN(savedLevel)) savedLevel = 0;

      if (currentLevel < savedLevel) {
        writeStorage(rewardStorageKey, String(currentLevel));
        savedLevel = currentLevel;
      }

      const compareLevel = Math.max(savedLevel, previousLevel);
      if (currentLevel <= compareLevel || currentLevel <= 0) return;

      if (finalReached && !previousFinalReached) {
        const title = root.dataset.popupSuccessTitle || 'You reached the target!';
        const message = root.dataset.popupSuccessMessage || 'Congratulations, your reward is unlocked.';
        showToast(title, message);
        markStep(steps[steps.length - 1]);
      } else {
        const step = achievedSteps[achievedSteps.length - 1];
        const rewardName = step?.dataset.stepLabel || '';
        const title = root.dataset.popupStepTitle || 'New reward unlocked!';
        const fallbackMessage = root.dataset.popupStepMessage || 'Congratulations! You reached [reward]';
        const customMessage = step?.dataset.stepPopupMessage || '';
        const message = (customMessage || fallbackMessage).replace(/\[reward\]/g, rewardName);
        showToast(title, message);
        markStep(step);
      }

      writeStorage(rewardStorageKey, String(currentLevel));
    }

    async function refreshCart(force = false) {
      if (state.refreshQueued && !force) return;
      state.refreshQueued = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Cart refresh failed');
        const cart = await response.json();
        const previousCents = state.currentCents;
        state.currentCents = Number(cart.total_price || 0);
        state.itemCount = Number(cart.item_count || 0);
        root.dataset.currentCents = String(state.currentCents);
        renderState();
        if (state.currentCents !== previousCents) evaluateRewards(previousCents);
      } catch (error) {
      } finally {
        state.refreshQueued = false;
      }
    }

    function queueRefresh(delay = 350) {
      window.clearTimeout(root._gtMbnRefreshTimer);
      root._gtMbnRefreshTimer = window.setTimeout(() => refreshCart(), delay);
    }

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement && form.action && form.action.includes('/cart/add')) {
        queueRefresh(800);
      }
    }, true);

    document.addEventListener('click', (event) => {
      const trigger = event.target?.closest?.('[name="add"], [data-add-to-cart], .product-form__submit');
      if (trigger) queueRefresh(900);
    }, true);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) refreshCart(true);
    });

    window.addEventListener('pageshow', () => refreshCart(true));
    window.addEventListener('gt:cart-updated', () => refreshCart(true));

    if (!window.__gtMbnFetchPatched) {
      const originalFetch = window.fetch;
      window.fetch = async function(...args) {
        const response = await originalFetch.apply(this, args);
        try {
          const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
          if (/\/cart\/(add|change|update|clear)(\.js)?/i.test(requestUrl)) {
            window.dispatchEvent(new CustomEvent('gt:cart-updated'));
          }
        } catch (error) {}
        return response;
      };
      window.__gtMbnFetchPatched = true;
    }

    if (!window.__gtMbnXHRPatched && window.XMLHttpRequest) {
      const OriginalOpen = XMLHttpRequest.prototype.open;
      const OriginalSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        this.__gtMbnUrl = url;
        return OriginalOpen.call(this, method, url, ...rest);
      };
      XMLHttpRequest.prototype.send = function(...args) {
        this.addEventListener('loadend', () => {
          try {
            if (/\/cart\/(add|change|update|clear)(\.js)?/i.test(this.__gtMbnUrl || '')) {
              window.dispatchEvent(new CustomEvent('gt:cart-updated'));
            }
          } catch (error) {}
        });
        return OriginalSend.apply(this, args);
      };
      window.__gtMbnXHRPatched = true;
    }

    state.liveIntervalId = window.setInterval(() => refreshCart(true), LIVE_INTERVAL);
    refreshCart(true);
  }

  function init() {
    document.querySelectorAll('[data-gt-mobile-bottom-nav]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-gt-mobile-bottom-nav]').forEach(setup);
  });
})();
